import React from 'react';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import {
  useNavReconOverviewExpanded,
  useNavReconPipelineMetadata,
} from '../../../../api/rs-hg/nav-recon/hooks/queries';
import type {
  NavReconOverviewObject,
  NavReconPipelineMetadata,
} from '../../../../api/rs-hg/nav-recon/types';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import NavReconOverviewExpandedTable from './components/NavReconOverviewExpandedTable';

/** Object payload so QueryBoundary does not treat an empty overview list as missing data. */
type NavReconOverviewExpandedLoaded = {
  overview: NavReconOverviewObject[];
  pipelineMetadata: NavReconPipelineMetadata[];
};

/**
 * NAV-Recon expanded overview — `NavReconOverviewObject` / `get_nav_recon_overview`.
 */
const NavReconOverviewExpandedPage: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = ({ positionDate, section, pageId }) => {
  const overview = useNavReconOverviewExpanded(positionDate);
  const pipelineMetadata = useNavReconPipelineMetadata(positionDate);

  const loaded: NavReconOverviewExpandedLoaded | undefined =
    overview.data && pipelineMetadata.data
      ? {
        overview: overview.data,
        pipelineMetadata: pipelineMetadata.data,
      }
      : undefined;

  const isOverviewLoading =
    !positionDate ||
    overview.isPending ||
    pipelineMetadata.isPending;

  return (
    <QueryBoundary<NavReconOverviewExpandedLoaded>
      isLoading={isOverviewLoading}
      isError={overview.isError || pipelineMetadata.isError}
      error={overview.error ?? pipelineMetadata.error}
      data={loaded}
      loadingMessages={[
        'Loading NAV-Recon overview…',
        'Fetching pipeline metadata…',
      ]}
      errorMessage="An error occurred while loading NAV-Recon overview."
      noDataMessage="No NAV-Recon overview data for the selected date."
    >
      {(data) => (
        <NavReconOverviewExpandedTable
          data={data.overview}
          pipelineMetadata={data.pipelineMetadata}
          positionDate={positionDate!}
          section={section}
          pageId={pageId}
        />
      )}
    </QueryBoundary>
  );
};

export default NavReconOverviewExpandedPage;
